import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-250 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-depth-1 hover:bg-primary/90 hover:shadow-depth-2 hover:-translate-y-[1px]',
        destructive:
          'bg-destructive text-destructive-foreground shadow-depth-1 hover:bg-destructive/90',
        outline:
          'border border-border/40 bg-card shadow-depth-1 hover:border-primary/30 hover:bg-muted/30 hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-card/60 hover:text-accent-foreground',
        link:
          'text-primary underline-offset-4 hover:underline',
        success:
          'bg-success text-success-foreground shadow-depth-1 hover:bg-success/90',
        soft:
          'bg-primary/8 text-primary hover:bg-primary/15 dark:bg-primary/15 dark:hover:bg-primary/25',
        glass:
          'glass text-foreground hover:shadow-depth-2 hover:-translate-y-[1px]',
        ambient:
          'bg-card border border-border/40 text-foreground shadow-soft-sm hover:border-primary/30 hover:bg-muted/30 hover:-translate-y-[1px]',
      },
      size: {
        default: 'h-10 px-4 py-2 min-h-[44px] sm:min-h-[40px]',
        sm: 'h-9 rounded-lg px-3 text-xs min-h-[36px]',
        lg: 'h-11 rounded-xl px-8 text-base min-h-[44px]',
        xl: 'h-12 rounded-xl px-10 text-base min-h-[48px]',
        icon: 'h-10 w-10 min-h-[40px] min-w-[40px]',
        'icon-sm': 'h-8 w-8 min-h-[32px] min-w-[32px] rounded-lg',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    // Radix `Slot` (asChild) requires EXACTLY ONE child. Injecting the
    // loader/leftIcon/rightIcon siblings turns children into an array and makes
    // `React.Children.only` throw ("expected to receive a single React element
    // child"), crashing the whole page. When asChild is set, pass the single
    // child straight through — the consumer composes any icons inside it.
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={disabled || loading}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin-slow" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
